import { Ability } from '@casl/ability';
import {
  Actions,
  ResourceForm,
  Subjects,
  AttributeSelection,
  GroupAttributeMatchMode,
} from '@dispatch/shared/types';
import {
  SectionWithMeta,
  SelectField,
  TextField,
  DateField,
} from '@dispatch/web/ui';
import {
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useState, useEffect, useRef } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { ResourceContext } from '../../ResourcesDetails';
import { AttributeAdd } from './AttributeAdd';
import { AttributesForm } from './AttributesForm';
import { Close, InfoOutlined } from '@mui/icons-material';
import { useGetGroupAttributes, useCompany } from '@dispatch/shared/hooks';
import { useCurrentUser, useAccessToken } from '@dispatch/web/auth';
import { useIsAuthenticated } from '@azure/msal-react';

// Group Attribute types
type GroupAttribute = {
  id: string;
  groupAttributeName: string;
  startDate?: string | null;
  endDate?: string | null;
};

type MasterGroupAttribute = {
  title: string;
  value: string;
};

export type AttributesProps = {
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceForm>;
  getValues: UseFormGetValues<ResourceForm>;
  errors: FieldErrors;
  setValue: UseFormSetValue<ResourceForm>;
  isReadOnly?: boolean;
};

export function AttributesTab({
  control,
  permissions,
  watch,
  getValues,
  errors,
  setValue,
  isReadOnly = false,
}: AttributesProps) {
  const { data } = useContext(ResourceContext);
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'attributes',
  });
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const canViewAttrGroupDefinitions = permissions?.can?.(
    'access',
    'attribute-group-definition'
  );

  const { company } = useCompany();
  const { tenantId } = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const { accessToken } = useAccessToken();

  const { groupAttributesData, groupAttributesRefetch } = useGetGroupAttributes(
    tenantId,
    !!tenantId && isAuthenticated
  );

  // Refetch group attributes when access token changes (token refresh)
  useEffect(() => {
    if (isAuthenticated && tenantId && accessToken.token) {
      groupAttributesRefetch();
    }
  }, [accessToken.token, isAuthenticated, tenantId, groupAttributesRefetch]);

  // Transform API response to dropdown options format
  const masterGroupAttributes: MasterGroupAttribute[] = (
    groupAttributesData || []
  ).map((groupAttr: { id: string; attributeGroupName: string }) => ({
    title: groupAttr.attributeGroupName, // Display name
    value: groupAttr.id, // Group ID as value
  }));

  const [selectedGroupAttributes, setSelectedGroupAttributes] = useState<
    GroupAttribute[]
  >([]);

  // Default to AttributeSelection.Single (normal view) if not set
  const [showGroupAttributes, setShowGroupAttributes] = useState(() => {
    return data?.attributeFilterSelection === AttributeSelection.Group;
  });

  // Track initial data loading to prevent marking form dirty during initialization
  const isInitialLoadingRef = useRef(true);

  // Track actual user interactions (add/remove/modify group attributes or toggle view)
  // This prevents false positive dirty marking when:
  // 1. Component loads with existing group attributes data
  // 2. showGroupAttributes becomes true during initial render
  // 3. Sync useEffect runs and would incorrectly mark form as dirty
  // Only mark dirty when user actually makes changes, not during data population
  const hasUserInteractedRef = useRef(false);

  // Populate selectedGroupAttributes from resource data when component loads
  useEffect(() => {
    // Treat backend refresh as a fresh load to avoid marking form dirty
    isInitialLoadingRef.current = true;
    hasUserInteractedRef.current = false;

    if (data?.selectedAttributeGroups) {
      const transformedGroupAttributes = data.selectedAttributeGroups.map(
        (groupAttr) => ({
          id: groupAttr.id, // Use the API ID directly
          groupAttributeName: groupAttr.displayName,
          startDate: groupAttr.startDate ?? null,
          endDate: groupAttr.endDate ?? null,
        })
      );
      setSelectedGroupAttributes(transformedGroupAttributes);
    } else {
      setSelectedGroupAttributes([]);
    }
    // Mark initial loading as complete after first data load
    setTimeout(() => {
      isInitialLoadingRef.current = false;
    }, 0);
  }, [data?.selectedAttributeGroups]);

  // Sync selectedGroupAttributes to form whenever it changes
  useEffect(() => {
    const formattedForAPI = selectedGroupAttributes.map((attr) => ({
      id: attr.id,
      displayName: attr.groupAttributeName,
      startDate: attr.startDate ?? null,
      endDate: attr.endDate ?? null,
    }));

    // Only mark dirty if:
    // 1. User is in group attributes view AND
    // 2. This is not initial data loading (user actually made a change) AND
    // 3. User has actually interacted (added/removed/modified group attributes)
    const shouldMarkDirty =
      showGroupAttributes &&
      !isInitialLoadingRef.current &&
      hasUserInteractedRef.current;

    setValue('selectedAttributeGroups', formattedForAPI, {
      shouldDirty: shouldMarkDirty,
      shouldTouch: shouldMarkDirty,
      shouldValidate: false,
    });
  }, [selectedGroupAttributes, setValue, showGroupAttributes]);

  // Update toggle state when data.attributeFilterSelection changes
  useEffect(() => {
    if (data?.attributeFilterSelection == null) {
      return;
    }

    setShowGroupAttributes(
      data.attributeFilterSelection === AttributeSelection.Group
    );
    setValue('attributeFilterSelection', data.attributeFilterSelection, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [data?.attributeFilterSelection, setValue]);

  const getTitle = () =>
    data?.name ? `${data?.name}'s Attributes` : 'Attributes';

  const handleGroupAttributeAdd = (value: unknown) => {
    const rawValue = value as { title: string; value: string };
    const newGroupAttribute: GroupAttribute = {
      id: rawValue.value, // Use the group attribute ID directly
      groupAttributeName: rawValue.title,
      startDate: null,
      endDate: null,
    };
    hasUserInteractedRef.current = true; // Mark as user interaction
    setSelectedGroupAttributes((prev) => [...prev, newGroupAttribute]);
  };

  const handleGroupAttributeRemove = (id: string) => {
    hasUserInteractedRef.current = true; // Mark as user interaction (de-assignment)
    setSelectedGroupAttributes((prev) => {
      const updated = prev.filter((attr) => attr.id !== id);
      // Force a new array reference to ensure React detects the change
      return [...updated];
    });
  };

  const handleGroupAttributeChange = (
    id: string,
    field: keyof GroupAttribute,
    value?: string | null
  ) => {
    hasUserInteractedRef.current = true; // Mark as user interaction
    setSelectedGroupAttributes((prev) => {
      const updated = prev.map((attr) =>
        attr.id === id ? { ...attr, [field]: value } : attr
      );

      // Also update the react-hook-form field directly so the DatePicker
      // reflects the cleared/null value immediately instead of reverting
      // to the last-saved value.
      const idx = updated.findIndex((a) => a.id === id);
      if (idx > -1) {
        // Update the whole selectedAttributeGroups value in the form (typed key)
        const formattedForAPI = updated.map((attr) => ({
          id: attr.id,
          displayName: attr.groupAttributeName,
          startDate: attr.startDate ?? null,
          endDate: attr.endDate ?? null,
        }));

        setValue('selectedAttributeGroups', formattedForAPI, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      }

      return updated;
    });
  };

  const getFilteredGroupAttributes = () => {
    // Filter out already selected group attributes by comparing group IDs
    const selectedGroupIds = selectedGroupAttributes.map(
      (attr) => attr.id // Use the ID for filtering
    );
    return masterGroupAttributes.filter(
      (master) => !selectedGroupIds.includes(master.value) // master.value is the group ID
    );
  };

  const renderGroupAttributesView = () => (
    <>
      <div hidden={!hasUpsertPermissions && !isReadOnly}>
        <div style={{ padding: '24px' }}>
          <Grid container rowSpacing={4} spacing={2}>
            <Grid data-testid="add-group-attribute" size={6}>
              <SelectField
                id="addSelectedGroupAttributeGUID"
                name="addSelectedGroupAttributeGUID"
                label="Add New Group Attribute"
                control={control}
                onValueChange={handleGroupAttributeAdd}
                options={getFilteredGroupAttributes()}
                disabled={isReadOnly}
              />
            </Grid>
          </Grid>
        </div>
        <Divider />
      </div>
      {selectedGroupAttributes.map((groupAttr, idx) => {
        const groupDefinition = (groupAttributesData || []).find(
          (groupData: { id: string }) => groupData.id === groupAttr.id
        ) as
          | {
              id: string;
              attributeGroupName: string;
              groupMap?: Record<string, Record<string, string>>;
              valueMatchModes?: Record<string, GroupAttributeMatchMode>;
            }
          | undefined;

        const groupDefinitionTooltip =
          canViewAttrGroupDefinitions && groupDefinition?.groupMap ? (
            <Box sx={{ p: 0.25 }}>
              {Object.entries(groupDefinition.groupMap).map(
                ([attrDefGuid, attrValues]) => {
                  const attrDefName =
                    company?.attributeDefinition?.find(
                      (attrDef) => attrDef.integrationGUID === attrDefGuid
                    )?.displayName ?? 'Unknown Attribute';
                  const valueDisplayNames = Object.values(attrValues);
                  const matchMode =
                    groupDefinition.valueMatchModes?.[attrDefGuid] ??
                    GroupAttributeMatchMode.ANY;
                  return (
                    <Stack
                      key={attrDefGuid}
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mb: 0.25 }}
                    >
                      <Typography sx={{ fontSize: '0.72rem' }}>
                        <strong>{attrDefName}:</strong>{' '}
                        {valueDisplayNames.join(', ')}
                      </Typography>
                      <Chip
                        label={matchMode}
                        size="small"
                        variant="outlined"
                        color={
                          matchMode === GroupAttributeMatchMode.ANY
                            ? 'primary'
                            : 'secondary'
                        }
                        sx={{
                          fontSize: '0.65rem',
                          height: '16px',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Stack>
                  );
                }
              )}
            </Box>
          ) : null;

        return (
          <div key={groupAttr.id} style={{ padding: '24px' }}>
            <Grid container spacing={4}>
              <Grid data-testid="group-attribute-name" size={6}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      id={`groupAttribute-${groupAttr.id}`}
                      name={`selectedAttributeGroups.${idx}.displayName`}
                      label="Group Attribute"
                      type="text"
                      control={control}
                      defaultValue={groupAttr.groupAttributeName}
                      inputProps={{
                        readOnly: true,
                        endAdornment: hasUpsertPermissions && (
                          <IconButton
                            disabled={isReadOnly}
                            onClick={() =>
                              handleGroupAttributeRemove(groupAttr.id)
                            }
                          >
                            <Close />
                          </IconButton>
                        ),
                      }}
                      disabled={isReadOnly}
                    />
                  </Box>
                  {groupDefinitionTooltip && (
                    <Tooltip
                      title={groupDefinitionTooltip}
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            boxShadow: 4,
                            border: '1px solid rgba(0,0,0,0.12)',
                            '& .MuiTooltip-arrow': {
                              color: 'background.paper',
                              '&::before': {
                                border: '1px solid rgba(0,0,0,0.12)',
                              },
                            },
                          },
                        },
                      }}
                    >
                      <IconButton size="small">
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Grid>
              <Grid size={3}>
                <DateField
                  id={`groupAttribute-startDate-${groupAttr.id}`}
                  name={`selectedAttributeGroups.${idx}.startDate`}
                  label="Start Date"
                  control={control}
                  onChange={(date) => {
                    const dateString =
                      date instanceof Date ? date.toISOString() : null;
                    handleGroupAttributeChange(
                      groupAttr.id,
                      'startDate',
                      dateString
                    );
                  }}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <DateField
                  id={`groupAttribute-endDate-${groupAttr.id}`}
                  name={`selectedAttributeGroups.${idx}.endDate`}
                  label="End Date"
                  control={control}
                  onChange={(date) => {
                    const dateString =
                      date instanceof Date ? date.toISOString() : null;
                    handleGroupAttributeChange(
                      groupAttr.id,
                      'endDate',
                      dateString
                    );
                  }}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
            <Divider style={{ marginTop: '16px' }} />
          </div>
        );
      })}
    </>
  );

  const renderNormalAttributesView = () => (
    <>
      <div hidden={!hasUpsertPermissions && !isReadOnly}>
        <AttributeAdd
          append={append}
          control={control}
          watch={watch}
          isReadOnly={isReadOnly}
        />
        <Divider />
      </div>
      {fields.map((field, index) => {
        return (
          <AttributesForm
            key={`${field.id}`}
            control={control}
            permissions={permissions}
            index={index}
            remove={remove}
            watch={watch}
            field={field}
            getValues={getValues}
            errors={errors && errors[index] ? errors[index] : null}
            setValue={setValue}
            isReadOnly={isReadOnly}
          />
        );
      })}
    </>
  );

  return (
    <div
      style={{
        padding: '24px',
        position: 'relative',
      }}
    >
      {/* Toggle positioned absolutely in top right */}
      <Box
        sx={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showGroupAttributes}
              onChange={(e) => {
                const isGroupView = e.target.checked;
                hasUserInteractedRef.current = true; // Mark as user interaction
                setShowGroupAttributes(isGroupView);
                // Update attributeFilterSelection enum in form data
                setValue(
                  'attributeFilterSelection',
                  isGroupView
                    ? AttributeSelection.Group
                    : AttributeSelection.Single,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  }
                );
              }}
              color="primary"
            />
          }
          label="Group Attributes"
          labelPlacement="start"
        />
      </Box>

      <SectionWithMeta title={getTitle()}>
        {showGroupAttributes
          ? renderGroupAttributesView()
          : renderNormalAttributesView()}
      </SectionWithMeta>
    </div>
  );
}
